import { AutoSuggestParams } from 'components/shared/auto-suggest/Types';
import { SingleDatePickerOptions } from 'components/shared/edit/fields';
import * as React from 'react';
import { DropdownOption } from 'shared-client/types/fields';
import { ModelStatus } from 'shared-client/types/model-status';
import { stripHtml } from 'util/StringUtil';
import { NotNullValue, NotNullValueLabel, NullValue, NullValueLabel } from './providers/Constants';
import { FilterType } from './providers/Types';

export { default as AvailableFilters } from './AvailableFilters';
export { default as FilterEdit } from './FilterEdit';
export { default as Filters } from './Filters';
export { default as SelectedFilters } from './SelectedFilters';

/**
 * Defines the shape of filter definitions.
 *
 * @export
 * @interface Filter
 */
export type Filter = {
	name: string;
	displayName: string;
	type: FilterType;
	nullable: boolean;
	options?: FilterOptions;
} & FilterKind;

export type FilterKind =
	| {
			implicit: true;
			permanent?: false;
			/**
			 * Whether this implicit filter should be shown when clicking a "Full Report" button
			 */
			shownOnRedirect?: boolean;
			value: any;
	  }
	| {
			implicit?: false;
			permanent: true;
			value: any;
	  }
	| { implicit?: false; permanent?: false };

export interface FilterOptions {
	baseUrl?: string;
	params?: AutoSuggestParams;
	dropdownValues?: DropdownOption[];
	nullValueLabel?: string;
	dateParams?: SingleDatePickerOptions;
	ignoreTimezone?: boolean;
}

/**
 * Returns an array of all filters used by models that inherit `BaseModel` on the server
 */
export function getBaseModelSearchFilters(ignored?: string[]): Filter[] {
	return getFilters(
		[getStatusSearchFilter(), ...getUserTrackedSearchFilters(), ...getBasicModelSearchFilters()],
		ignored,
	);
}

/**
 * Returns an array of Create Date and Modify Date by models that inherit `BasicModel` on the server
 */
export function getBasicModelSearchFilters(ignored?: string[]): Filter[] {
	return getFilters(
		[
			{
				name: 'CreateDate',
				displayName: 'Create Date',
				type: FilterType.MultipleDateRangePreset,
				nullable: false,
			},
			{
				name: 'ModifyDate',
				displayName: 'Modify Date',
				type: FilterType.MultipleDateRangePreset,
				nullable: false,
			},
		],
		ignored,
	);
}

/**
 * Extended AutoSuggestItem type that includes additional values passed to the Scope filter
 */
type ScopeFilterParams = AutoSuggestParams & {
	ignoreStatus: boolean;
};

/**
 * Returns a scope filter
 */
export function getScopeSearchFilter(multiSiteEnabled: boolean): Filter[] {
	if (multiSiteEnabled) {
		return [
			{
				name: 'Scope',
				displayName: 'Scope',
				type: FilterType.MultipleAutoSuggest,
				nullable: true,
				options: {
					baseUrl: '/ajax/scope',
					nullValueLabel: 'Global',
					params: {
						ignoreStatus: false,
					} as ScopeFilterParams,
				},
			},
		];
	}

	return [];
}

/**
 * Returns an array of filters used by models that inherit `IUserTrackedModel` interface on the server
 */
export function getUserTrackedSearchFilters(): Filter[] {
	return [
		{
			name: 'CreateUser',
			displayName: 'Created By',
			type: FilterType.MultipleAutoSuggest,
			nullable: true,
			options: {
				baseUrl: '/ajax/admins',
				nullValueLabel: 'System',
			},
		},
		{
			name: 'ModifyUser',
			displayName: 'Modified By',
			type: FilterType.MultipleAutoSuggest,
			nullable: true,
			options: {
				baseUrl: '/ajax/admins',
				nullValueLabel: 'System',
			},
		},
	];
}

/**
 * Returns an array of all filters that can be used on VersionableModel entities by default.
 */
export function getVersionableModelSearchFilters(ignored?: string[]): Filter[] {
	return getFilters(
		[
			...getBaseModelSearchFilters(),
			getHasDraftSearchFilter(),
			getHasScheduledPublishSearchFilter(),
			getHasScheduledDeactivationSearchFilter(),
			getHasScheduledDeletionSearchFilter(),
		],
		ignored,
	);
}

/**
 * Returns an array of all filters used by models that implement `INamedModel` on the server
 */
export function getNameSearchFilter(): Filter {
	return {
		name: 'Name',
		displayName: 'Name',
		type: FilterType.MultipleText,
		nullable: false,
	};
}

/**
 * Returns Code filter used by models that implement `IUniqueCodeModel` on the server
 */
export const getCodeSearchFilter = (): Filter => {
	return {
		name: 'CodeFilter',
		displayName: 'Code',
		type: FilterType.MultipleText,
		nullable: false,
	};
};

/**
 * Returns a filter used by models that implement `IPermanentModel` on the server
 */
export function getPermanentSearchFilter(): Filter {
	return {
		name: 'IsPermanent',
		displayName: 'Is Permanent',
		type: FilterType.SingleBoolean,
		nullable: false,
	};
}

export function getUserSearchFilter(): Filter {
	return {
		name: 'User',
		displayName: 'User',
		type: FilterType.MultipleAutoSuggest,
		nullable: true,
		options: {
			baseUrl: '/ajax/users',
			nullValueLabel: 'System',
		},
	};
}

export function getNullableUserSearchFilter(): Filter {
	return {
		name: 'User',
		displayName: 'User',
		type: FilterType.MultipleAutoSuggest,
		nullable: true,
		options: {
			baseUrl: '/ajax/users',
			nullValueLabel: 'System',
		},
	};
}

/**
 * Returns a basic Parent filter for hierarchical entities
 */
export function getParentSearchFilter(autoSuggestBaseUrl: string): Filter {
	return {
		name: 'Parent',
		displayName: 'Parent',
		type: FilterType.MultipleAutoSuggest,
		nullable: true,
		options: {
			baseUrl: autoSuggestBaseUrl,
		},
	};
}

/**
 * Updates parent filter baseUrl by adding parent entity id
 */
export function updateParentFilterBaseUrl(parentEntityId: string, filters: Filter[]) {
	const parentFilter = filters.find((f) => f.name === 'Parent');
	if (parentFilter?.options?.baseUrl) {
		parentFilter.options.baseUrl += '/' + parentEntityId;
	}
}

/**
 * Returns an array of all filters used by models that implement `IExternalModel` on the server
 */
export function getExternalIdSearchFilter(): Filter {
	return {
		name: 'ExternalId',
		displayName: 'External ID',
		type: FilterType.MultipleText,
		nullable: false,
	};
}

/**
 * Returns a filter to get records with/out drafts only
 */
export function getHasDraftSearchFilter(): Filter {
	return {
		name: 'HasDraft',
		displayName: 'Has Draft',
		type: FilterType.SingleBoolean,
		nullable: false,
	};
}

/**
 * Returns a filter to get records with/out scheduled publish records only
 */
export function getHasScheduledPublishSearchFilter(): Filter {
	return {
		name: 'HasScheduledPublish',
		displayName: 'Has Scheduled Publish',
		type: FilterType.SingleBoolean,
		nullable: false,
	};
}

/**
 * Returns a filter to get records with/out scheduled deactivations only
 */
export function getHasScheduledDeactivationSearchFilter(): Filter {
	return {
		name: 'HasScheduledDeactivation',
		displayName: 'Has Scheduled Deactivation',
		type: FilterType.SingleBoolean,
		nullable: false,
	};
}

/**
 * Returns a filter to get records with selected status only
 */
export function getStatusSearchFilter(ignoredDropDownValues?: DropdownOption[]): Filter {
	return {
		name: 'Status',
		displayName: 'Status',
		type: FilterType.MultipleDropdown,
		nullable: false,
		options: {
			dropdownValues: getDropDownValues(
				[
					{ value: ModelStatus.Published, text: ModelStatus.Published },
					{ value: ModelStatus.Inactive, text: ModelStatus.Inactive },
					{ value: ModelStatus.Deleted, text: ModelStatus.Deleted },
				],
				ignoredDropDownValues,
			),
		},
	};
}

/**
 * Returns a filter to get records with/out scheduled deletions only
 */
export function getHasScheduledDeletionSearchFilter(): Filter {
	return {
		name: 'HasScheduledDeletion',
		displayName: 'Has Scheduled Deletion',
		type: FilterType.SingleBoolean,
		nullable: false,
	};
}

/**
 * Returns a filter to get records by the URL field only.
 */
export function getUrlSearchFilter(): Filter {
	return {
		name: 'Url',
		displayName: 'URL',
		type: FilterType.MultipleText,
		nullable: false,
	};
}

/**
 * Returns an array of all filters used by models that implement `ISeoModel` on the server
 */
export function getSeoSearchFilters(ignored?: string[]): Filter[] {
	return getFilters(
		[
			getUrlSearchFilter(),
			{
				name: 'PageTitle',
				displayName: 'Page Title',
				type: FilterType.MultipleText,
				nullable: false,
			},
		],
		ignored,
	);
}

/**
 * Defines the shape of filter definition along with the value that was picked. Uses `any` for the type
 * as getting this to go all the way down to where it would be useful is really hard, and generally not worth it, as most
 * of the places that reference this directly will not care about the type.
 *
 * @export
 * @type Filter
 */
export type SelectedFilter = Filter & {
	value: any;
};

/** Reusable definition for the empty string */
export function Empty() {
	return <em>&lt;Empty&gt;</em>;
}
export function FilterNotSet() {
	return <em>&lt;Not Set&gt;</em>;
}

export function FilterString(value: string, options?: FilterOptions) {
	switch (value) {
		case NullValue:
		case NullValueLabel:
			return (options && options.nullValueLabel) || NullValueLabel;
		case NotNullValue:
			return NotNullValueLabel;
		default:
			return stripHtml(value);
	}
}

function getFilters(filters: Filter[], ignored?: string[]) {
	if (!ignored) {
		return filters;
	}
	return filters.filter((f) => !ignored.find((i) => i === f.name));
}

export function getDropDownValues(dropDownValues: DropdownOption[], ignoredDropDownValues?: DropdownOption[]) {
	if (!ignoredDropDownValues) {
		return dropDownValues;
	}
	return dropDownValues.filter((f) => !ignoredDropDownValues.find((i) => i.value === f.value && i.text === f.text));
}
